"use client";

/**
 * Reusable Form Footer Component
 * 
 * Provides consistent footer buttons for forms based on mode:
 * - 'add': Cancel and Add buttons
 * - 'edit': Cancel and Save buttons
 * - 'view': Close and Edit buttons
 * 
 * @example
 * // Add mode
 * <FormFooter
 *   mode="add"
 *   onCancel={handleCancel}
 *   onSubmit={handleSubmit}
 *   loading={isLoading}
 * />
 * 
 * // Edit mode
 * <FormFooter
 *   mode="edit"
 *   onCancel={handleCancel}
 *   onSubmit={handleSubmit}
 *   loading={isLoading}
 * />
 * 
 * // View mode
 * <FormFooter
 *   mode="view"
 *   onClose={handleClose}
 *   onEdit={handleEdit}
 * />
 */

import { X, Save, Plus, Edit, Loader2 } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import styles from "./FormFooter.module.css";

export type FormMode = 'add' | 'edit' | 'view';

interface FormFooterProps {
  mode: FormMode;
  // Common props
  loading?: boolean;
  disabled?: boolean;
  // Props for add/edit modes
  onCancel?: () => void;
  onSubmit?: () => void;
  // Props for view mode
  onClose?: () => void;
  onEdit?: () => void;
  // Permission checks
  requiredPermission?: string | string[]; // Permission cần thiết để hiển thị nút Edit (view mode) hoặc Add/Edit buttons
  // Custom button labels (optional)
  cancelLabel?: string;
  submitLabel?: string;
  closeLabel?: string;
  editLabel?: string;
  addLabel?: string;
  saveLabel?: string;
}

export default function FormFooter({
  mode,
  loading = false,
  disabled = false,
  onCancel,
  onSubmit,
  onClose,
  onEdit,
  requiredPermission,
  cancelLabel,
  submitLabel,
  closeLabel,
  editLabel,
  addLabel,
  saveLabel
}: FormFooterProps) {
  const { hasPermission } = usePermissions();
  
  // Add mode: Cancel + Add
  if (mode === 'add') {
    // Kiểm tra permission cho nút Add
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return null; // Không hiển thị footer nếu không có quyền
    }
    return (
      <div className={styles.footer}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onCancel}
          disabled={loading}
        >
          {cancelLabel || 'Hủy'}
        </button>
        <button
          type={onSubmit ? "button" : "submit"}
          className={styles.submitButton}
          onClick={onSubmit || undefined}
          disabled={loading || disabled}
        >
          {loading ? (
            <>
              <Loader2 size={20} className={styles.loadingSpinner} />
              Đang tạo...
            </>
          ) : (
            <>
              <Plus size={20} />
              {addLabel || 'Thêm'}
            </>
          )}
        </button>
      </div>
    );
  }

  // Edit mode: Cancel + Save
  if (mode === 'edit') {
    // Kiểm tra permission cho nút Save
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return null; // Không hiển thị footer nếu không có quyền
    }
    return (
      <div className={styles.footer}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onCancel}
          disabled={loading}
        >
          {cancelLabel || 'Hủy'}
        </button>
        <button
          type={onSubmit ? "button" : "submit"}
          className={styles.submitButton}
          onClick={onSubmit || undefined}
          disabled={loading || disabled}
        >
          {loading ? (
            <>
              <Loader2 size={20} className={styles.loadingSpinner} />
              Đang lưu...
            </>
          ) : (
            <>
              <Save size={20} />
              {saveLabel || 'Lưu'}
            </>
          )}
        </button>
      </div>
    );
  }

  // View mode: Close + Edit
  const canEdit = !requiredPermission || hasPermission(requiredPermission);
  
  return (
    <div className={styles.footer}>
      <button
        type="button"
        className={styles.cancelButton}
        onClick={onClose}
      >
        {closeLabel || 'Đóng'}
      </button>
      {canEdit && (
        <button
          type="button"
          className={styles.submitButton}
          onClick={onEdit}
        >
          <Edit size={20} />
          {editLabel || 'Chỉnh sửa'}
        </button>
      )}
    </div>
  );
}

